/**
 * Entraînement CCS 2R — Mode examen blanc chronométré
 * Route : /enseignant/entrainement-ccs
 *
 * Simulation complète de l'épreuve orale CCS :
 *  - Tirage d'un cas au sort (parmi les 27, ou filtré par famille via ?famille=)
 *  - 3 phases successives chronométrées :
 *     · Préparation (20 min) : tableau de construction + notes
 *     · Présentation au tableau blanc (20 min) : checklist déroulé
 *     · Échange jury (20 min) : questions probables
 *  - Notifications sonores : début, mi-temps, dernière minute, fin
 *  - Notes persistées dans localStorage (durée 24h)
 *
 * Aucune authentification requise (zone publique gated par le Layout teacher).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Play, Pause, RotateCcw, SkipForward, Timer, Target, ArrowLeft, ArrowRight,
  CheckCircle2, BookOpen, Volume2, VolumeX, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  FAMILIES,
  getFamilyByCas,
  getFamilyBySlug,
  PHASES_CONSTRUCTION,
  EPREUVE_PHASES,
  REACTION_RAPIDE,
} from '../data/ccs2r/classeur7Familles';
import useSeo from '../hooks/useSeo';

// ─── Utils ───────────────────────────────────────────────────────────────────
function formatMMSS(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function pickRandomCas(allowedCas) {
  return allowedCas[Math.floor(Math.random() * allowedCas.length)];
}

// Tous les numéros de cas (1..27)
const ALL_CAS_NUMBERS = Array.from({ length: 27 }, (_, i) => i + 1);

// Bip sonore léger (WebAudio) — pas de fichier nécessaire
function playBeep(frequency = 880, durationMs = 200) {
  if (typeof window === 'undefined') return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000 + 0.05);
  } catch { /* noop */ }
}

const LS_NOTES = 'c2r:ccs2r-train-notes';

// ─── Composant : Chronomètre principal ───────────────────────────────────────
function PhaseTimer({ phase, isActive, onComplete, soundOn }) {
  const [remaining, setRemaining] = useState(phase.durationSec);
  const [running, setRunning] = useState(false);
  const tickRef = useRef(null);
  const lastMilestone = useRef(null);

  useEffect(() => {
    // Reset au changement de phase
    setRemaining(phase.durationSec);
    setRunning(false);
    lastMilestone.current = null;
  }, [phase.id, phase.durationSec]);

  useEffect(() => {
    if (!running) {
      if (tickRef.current) clearInterval(tickRef.current);
      return undefined;
    }
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 0) return 0;
        const next = r - 1;
        // Milestones : mi-temps, 1 minute, fin
        const halfway = Math.floor(phase.durationSec / 2);
        if (soundOn) {
          if (next === halfway && lastMilestone.current !== 'half') {
            lastMilestone.current = 'half';
            playBeep(660, 200);
          } else if (next === 60 && lastMilestone.current !== 'one-min') {
            lastMilestone.current = 'one-min';
            playBeep(880, 300);
          } else if (next === 0 && lastMilestone.current !== 'end') {
            lastMilestone.current = 'end';
            playBeep(1000, 600);
          }
        }
        if (next === 0) {
          setTimeout(() => onComplete?.(), 100);
        }
        return next;
      });
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [running, phase.durationSec, soundOn, onComplete]);

  const progress = 1 - remaining / phase.durationSec;
  const isLastMinute = remaining > 0 && remaining <= 60;
  const isFinished = remaining === 0;

  return (
    <div
      className="mc-card p-5 sm:p-6 relative overflow-hidden"
      style={{ borderTop: `4px solid ${phase.color}` }}
      data-testid={`timer-${phase.id}`}
      data-active={isActive}
    >
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <div
            className="text-[10px] font-black uppercase tracking-wider"
            style={{ color: phase.color }}
          >
            Phase {EPREUVE_PHASES.findIndex((p) => p.id === phase.id) + 1} / 3
          </div>
          <h3 className="text-xl sm:text-2xl font-black tracking-tight">{phase.label}</h3>
        </div>
        <Badge
          className="text-[10px] font-bold"
          style={{ background: `${phase.color}1a`, color: phase.color, border: 'none' }}
        >
          20 minutes
        </Badge>
      </div>

      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
        {phase.description}
      </p>

      {/* Chronomètre */}
      <div
        className="text-center py-5 rounded-xl mb-3 relative overflow-hidden"
        style={{
          background: isFinished
            ? `${phase.color}1a`
            : isLastMinute
              ? '#dc26261a'
              : 'hsl(var(--muted) / 0.4)',
        }}
      >
        <div
          className="text-5xl sm:text-6xl font-black tabular-nums tracking-tighter"
          style={{
            color: isFinished
              ? phase.color
              : isLastMinute
                ? '#dc2626'
                : 'inherit',
            fontFamily: "'Source Code Pro', monospace",
          }}
          data-testid={`timer-display-${phase.id}`}
        >
          {formatMMSS(remaining)}
        </div>
        {isLastMinute && !isFinished && (
          <div className="text-[10px] font-black uppercase tracking-widest text-[#dc2626] mt-1 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Dernière minute
          </div>
        )}
        {isFinished && (
          <div className="text-[10px] font-black uppercase tracking-widest mt-1" style={{ color: phase.color }}>
            ✓ Phase terminée
          </div>
        )}
        {/* Barre de progression */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-border/50">
          <div
            className="h-full transition-all duration-1000 ease-linear"
            style={{
              width: `${progress * 100}%`,
              background: isLastMinute && !isFinished ? '#dc2626' : phase.color,
            }}
          />
        </div>
      </div>

      {/* Contrôles */}
      <div className="flex flex-wrap gap-2 justify-center">
        <Button
          onClick={() => setRunning((r) => !r)}
          disabled={isFinished}
          data-testid={`btn-toggle-${phase.id}`}
          className="gap-1.5"
          style={{ background: phase.color, color: 'white' }}
          size="sm"
        >
          {running ? (
            <><Pause className="w-4 h-4" /> Pause</>
          ) : (
            <><Play className="w-4 h-4 fill-current" /> {remaining === phase.durationSec ? 'Démarrer' : 'Reprendre'}</>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setRemaining(phase.durationSec);
            setRunning(false);
            lastMilestone.current = null;
          }}
          data-testid={`btn-reset-${phase.id}`}
          className="gap-1.5"
        >
          <RotateCcw className="w-4 h-4" /> Reset
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setRemaining(0); setRunning(false); onComplete?.(); }}
          data-testid={`btn-skip-${phase.id}`}
          className="gap-1.5"
          disabled={isFinished}
        >
          <SkipForward className="w-4 h-4" /> Passer
        </Button>
      </div>

      {/* Checklist contextuelle */}
      <details className="mt-5 group" data-testid={`checklist-${phase.id}`}>
        <summary className="cursor-pointer text-sm font-bold hover:text-[hsl(var(--primary))] flex items-center gap-1 list-none">
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
          Checklist à dérouler ({phase.checklist.length} points)
        </summary>
        <ul className="mt-3 space-y-1.5">
          {phase.checklist.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-xs sm:text-sm">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

// ─── Page principale ────────────────────────────────────────────────────────
export default function EntrainementCCS() {
  const [searchParams] = useSearchParams();
  const filterFamilySlug = searchParams.get('famille');
  const filterFamily = filterFamilySlug ? getFamilyBySlug(filterFamilySlug) : null;

  useSeo({
    title: 'Entraînement CCS 2R · Examen blanc chronométré',
    description:
      "Simulation complète de l'oral CCS : tirage d'un cas au sort + 20 minutes de préparation + 20 minutes de présentation + 20 minutes d'échange avec le jury.",
    path: '/enseignant/entrainement-ccs',
  });

  const allowedCas = useMemo(
    () => (filterFamily ? filterFamily.cas : ALL_CAS_NUMBERS),
    [filterFamily],
  );

  const [casNum, setCasNum] = useState(null);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [notes, setNotes] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      const raw = window.localStorage.getItem(LS_NOTES);
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      // Notes périmées après 24h
      if (Date.now() - parsed.savedAt > 24 * 3600 * 1000) return '';
      return parsed.value || '';
    } catch { return ''; }
  });

  // Persister les notes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LS_NOTES, JSON.stringify({ value: notes, savedAt: Date.now() }));
    } catch { /* noop */ }
  }, [notes]);

  const currentPhase = EPREUVE_PHASES[phaseIdx];
  const family = casNum ? getFamilyByCas(casNum) : null;

  const handleDraw = () => {
    const next = pickRandomCas(allowedCas);
    setCasNum(next);
    setPhaseIdx(0);
    if (soundOn) playBeep(550, 150);
  };

  const handlePhaseComplete = () => {
    if (phaseIdx < EPREUVE_PHASES.length - 1) {
      setPhaseIdx(phaseIdx + 1);
      if (soundOn) setTimeout(() => playBeep(1100, 300), 200);
    }
  };

  const handleReset = () => {
    setCasNum(null);
    setPhaseIdx(0);
  };

  return (
    <article className="space-y-8" data-testid="entrainement-ccs-page">
      {/* Breadcrumb */}
      <Link
        to="/enseignant/classeur-7-familles"
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground"
        data-testid="back-to-classeur"
      >
        <ArrowLeft className="w-4 h-4" /> Classeur 7 familles
      </Link>

      {/* HEADER */}
      <header className="mc-card relative overflow-hidden p-6 sm:p-8" style={{ borderLeft: '6px solid #cf2031' }}>
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: '#cf2031' }}
          aria-hidden="true"
        />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#cf2031]/15 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[#cf2031] mb-3">
            <Timer className="w-3.5 h-3.5" /> Examen blanc · CCS 2 Roues
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Entraînement chronométré
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-3xl leading-relaxed">
            Conditions réelles. Tirage d'un cas au sort, puis 3 phases de 20 minutes :{' '}
            <strong>préparation</strong>, <strong>présentation au tableau</strong>,{' '}
            <strong>échange avec le jury</strong>.
            {filterFamily && (
              <>
                {' '}— Filtré sur la famille « <strong>{filterFamily.title}</strong> »
                ({filterFamily.cas.length} cas).
              </>
            )}
          </p>

          <div className="flex items-center gap-2 mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSoundOn((s) => !s)}
              className="gap-1.5"
              data-testid="sound-toggle"
            >
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <span className="text-xs">Bips {soundOn ? 'activés' : 'désactivés'}</span>
            </Button>
            {filterFamily && (
              <Button asChild variant="ghost" size="sm" className="gap-1.5" data-testid="clear-filter">
                <Link to="/enseignant/entrainement-ccs">
                  Retirer le filtre
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* TIRAGE AU SORT */}
      {!casNum ? (
        <>
          {/* Cartes "Autres modes d'entraînement" */}
          <section className="grid sm:grid-cols-2 gap-3" data-testid="alt-modes-section">
            <Link
              to="/enseignant/simulateur-ccs"
              className="mc-card p-4 group hover:-translate-y-0.5 hover:shadow-lg transition-all"
              style={{ borderLeft: '4px solid #cf2031' }}
              data-testid="alt-mode-simulateur"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#cf2031]/15 text-[#cf2031] flex-shrink-0">
                  <Target className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#cf2031]">
                    Mode guidé · Wizard
                  </div>
                  <div className="font-black text-sm sm:text-base">Simulateur 6 modules</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">
                    Tirage → famille → scénario → tableau → 5 questions → correction
                  </div>
                </div>
              </div>
            </Link>
            <Link
              to="/enseignant/banque-questions-jury"
              className="mc-card p-4 group hover:-translate-y-0.5 hover:shadow-lg transition-all"
              style={{ borderLeft: '4px solid #7c3aed' }}
              data-testid="alt-mode-banque"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#7c3aed]/15 text-[#7c3aed] flex-shrink-0">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#7c3aed]">
                    Mode révision · Flashcards
                  </div>
                  <div className="font-black text-sm sm:text-base">Banque de 105 questions</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">
                    Recherche, filtres, tirage de 10 questions, réponses-modèle
                  </div>
                </div>
              </div>
            </Link>
          </section>

          <Card className="border-dashed border-2" data-testid="draw-section">
          <CardContent className="p-8 sm:p-12 text-center">
            <Target className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[#cf2031] mb-4" />
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Prêt ? Tirez votre cas.
            </h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              {filterFamily
                ? `Un cas sera tiré au sort parmi les ${filterFamily.cas.length} cas de la famille « ${filterFamily.title} ».`
                : 'Un cas sera tiré au hasard parmi les 27 études de cas officielles.'}
            </p>
            <Button
              size="lg"
              onClick={handleDraw}
              className="gap-2 text-base"
              style={{ background: '#cf2031' }}
              data-testid="btn-draw"
            >
              <Target className="w-5 h-5" /> Tirer un cas au sort
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
          {/* CAS TIRÉ */}
          <section
            className="mc-card p-6 relative overflow-hidden"
            style={{ borderLeft: `6px solid ${family.accent}` }}
            data-testid="drawn-cas"
          >
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  Votre cas
                </div>
                <h2
                  className="text-3xl sm:text-4xl font-black tracking-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  data-testid="drawn-cas-num"
                >
                  Cas n°{casNum}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge
                    className="text-xs font-bold"
                    style={{ background: family.accent, color: 'white', border: 'none' }}
                    data-testid="drawn-family-badge"
                  >
                    Famille · {family.title}
                  </Badge>
                  <span className="text-xs text-muted-foreground italic">« {family.aspect} »</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  data-testid="open-family-fiche"
                >
                  <Link to={`/enseignant/classeur-7-familles/${family.slug}`} target="_blank" rel="noopener">
                    <BookOpen className="w-4 h-4" /> Ouvrir la fiche famille
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="gap-1.5 text-muted-foreground"
                  data-testid="btn-redraw"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Retirer un autre cas
                </Button>
              </div>
            </div>

            {/* Aide rapide réaction */}
            <details className="mt-5 group" data-testid="reaction-rapide-help">
              <summary className="cursor-pointer text-sm font-bold hover:text-[hsl(var(--primary))] flex items-center gap-1 list-none">
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                Méthode 20 minutes — Réaction rapide ({REACTION_RAPIDE.length} étapes)
              </summary>
              <ol className="mt-3 grid sm:grid-cols-2 gap-2">
                {REACTION_RAPIDE.map((r) => (
                  <li
                    key={r.step}
                    className="flex items-start gap-2 p-2.5 rounded-md bg-background/60 border border-border/40 text-xs"
                  >
                    <div
                      className="w-6 h-6 rounded-full text-white font-black flex items-center justify-center flex-shrink-0"
                      style={{ background: family.accent }}
                    >
                      {r.step}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold">{r.label}</div>
                      <div className="text-muted-foreground">{r.hint}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </details>
          </section>

          {/* PROGRESSION DES 3 PHASES */}
          <section className="space-y-3" data-testid="phases-tracker">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1.5">
                {EPREUVE_PHASES.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {i > 0 && <div className="w-6 h-px bg-border" aria-hidden="true" />}
                    <div
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${
                        i === phaseIdx
                          ? 'border-2'
                          : i < phaseIdx
                            ? 'bg-muted/40 border-muted text-muted-foreground'
                            : 'border-dashed border-border text-muted-foreground'
                      }`}
                      style={i === phaseIdx ? { borderColor: p.color, color: p.color } : undefined}
                      data-testid={`tracker-${p.id}`}
                    >
                      {i < phaseIdx && <CheckCircle2 className="w-3 h-3" />}
                      <span>{p.label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </section>

          {/* TIMER PHASE ACTIVE */}
          <PhaseTimer
            phase={currentPhase}
            isActive
            onComplete={handlePhaseComplete}
            soundOn={soundOn}
          />

          {/* NOTES + TABLEAU DE CONSTRUCTION (preparation only) */}
          {phaseIdx === 0 && (
            <>
              <section className="mc-card p-5" data-testid="notes-section">
                <h3 className="text-sm font-black uppercase tracking-wider text-muted-foreground mb-2">
                  📝 Mes notes de préparation
                </h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={8}
                  className="w-full p-3 rounded-md border bg-background text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  placeholder={`Cas n°${casNum} — ${family.title}\n\nObjectifs :\n  · Connaître : \n  · Comprendre : \n  · Agir : \n\nSupports : \nLeviers : \nFreins : \nÉvaluation : `}
                  data-testid="notes-textarea"
                />
                <div className="text-[10px] text-muted-foreground mt-1 italic">
                  Notes sauvegardées localement, périmées après 24h.
                </div>
              </section>

              <section className="space-y-3" data-testid="construction-table-section">
                <h3 className="text-base font-black tracking-tight">
                  Tableau de construction (à recopier au tableau blanc)
                </h3>
                <div className="mc-card overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr style={{ background: `${family.accent}0d`, borderBottom: `2px solid ${family.accent}33` }}>
                        <th className="text-left px-2 py-2 font-black uppercase tracking-wider">Phase</th>
                        <th className="text-left px-2 py-2 font-black uppercase tracking-wider">Ce que je fais</th>
                        <th className="text-left px-2 py-2 font-black uppercase tracking-wider">Objectif</th>
                        <th className="text-left px-2 py-2 font-black uppercase tracking-wider">Activité</th>
                        <th className="text-left px-2 py-2 font-black uppercase tracking-wider">Support</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PHASES_CONSTRUCTION.map((p, i) => (
                        <tr key={p.phase} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                          <td className="px-2 py-1.5 font-bold" style={{ color: family.accent }}>{p.phase}</td>
                          <td className="px-2 py-1.5">{p.fait}</td>
                          <td className="px-2 py-1.5">{p.objectif}</td>
                          <td className="px-2 py-1.5">{p.activite}</td>
                          <td className="px-2 py-1.5">{p.support}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {/* Phase jury → rappel mots-clés */}
          {phaseIdx === 2 && (
            <section
              className="mc-card p-5"
              style={{ borderLeft: `4px solid ${family.accent}` }}
              data-testid="jury-keywords-section"
            >
              <h3 className="text-sm font-black uppercase tracking-wider mb-2" style={{ color: family.accent }}>
                Mots-clés à mobiliser face au jury
              </h3>
              <div className="flex flex-wrap gap-2">
                {family.motsClesJury.map((m) => (
                  <Badge
                    key={m}
                    className="text-sm font-bold py-1.5 px-3"
                    style={{ background: family.accent, color: 'white', border: 'none' }}
                  >
                    {m}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {/* Fin examen */}
          {phaseIdx === EPREUVE_PHASES.length - 1 && (
            <Card className="border-dashed text-center" data-testid="end-section">
              <CardContent className="p-6">
                <CheckCircle2 className="w-12 h-12 text-[#16a34a] mx-auto mb-3" />
                <h3 className="text-lg font-black mb-1">Bonne préparation !</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-xl mx-auto">
                  Une fois les 3 phases terminées, vous pouvez en relancer un autre pour
                  varier les familles et les cas.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button onClick={handleDraw} className="gap-1.5" style={{ background: '#cf2031' }}>
                    <Target className="w-4 h-4" /> Tirer un autre cas
                  </Button>
                  <Button asChild variant="outline" className="gap-1.5">
                    <Link to="/enseignant?tab=examen">
                      <ArrowLeft className="w-4 h-4" /> Retour Espace Pro
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </article>
  );
}
